import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { PhotoPicker } from '../primitives/PhotoPicker.js';

interface Props {
  onClose: () => void;
  initial?: {
    id?: string;
    name?: string;
    category?: string | null;
    compliance?: string;
    phone?: string | null;
    address?: string | null;
    email?: string | null;
    photo?: string | null;
    tags?: string[];
    contacts?: Array<{ name: string; role?: string | null; phone?: string | null; email?: string | null }>;
  };
}

export function AddSupplierModal({ onClose, initial }: Props) {
  const qc = useQueryClient();
  const editing = !!initial?.id;
  const [name, setName] = useState(initial?.name ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [compliance, setCompliance] = useState(initial?.compliance ?? 'pending');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [photo, setPhoto] = useState<string | null>(initial?.photo ?? null);
  const [preferred, setPreferred] = useState<boolean>(Array.isArray(initial?.tags) ? initial!.tags!.includes('preferred') : false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      if (!trimmedName) throw new Error('Name required');
      const trimmedCategory = category.trim();
      if (!trimmedCategory) throw new Error('Category required');
      const body = {
        name: trimmedName,
        category: trimmedCategory,
        compliance,
        phone: phone.trim() || null,
        email: email.trim() || null,
        address: address.trim() || null,
        photo: photo ?? null,
        tags: preferred ? ['preferred'] : [],
        contacts: initial?.contacts ?? [],
      };
      if (editing && initial?.id) return api.patch(`/suppliers/${initial.id}`, body);
      return api.post('/suppliers', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success(editing ? 'Supplier updated' : 'Supplier added', name);
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.message ?? e?.message ?? 'Check fields and try again'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="asup-title">{editing ? '✏ Edit Supplier' : '+ Add Supplier'}</div>
        <div className="form-row">
          <label className="form-label">Logo / Photo <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— optional, click / drop / paste URL</span></label>
          <PhotoPicker value={photo} onChange={setPhoto} />
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Company Name</label><input type="text" className="form-input" id="asup-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Category</label><input type="text" className="form-input" id="asup-category" value={category ?? ''} onChange={(e) => setCategory(e.target.value)} /></div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Compliance</label>
            <select className="form-select" id="asup-compliance" value={compliance} onChange={(e) => setCompliance(e.target.value)}>
              <option value="compliant">Compliant</option>
              <option value="pending">Pending</option>
              <option value="non-compliant">Non-Compliant</option>
            </select>
          </div>
          <div className="form-row"><label className="form-label">Phone</label><input type="tel" className="form-input" id="asup-phone" value={phone ?? ''} onChange={(e) => setPhone(e.target.value)} /></div>
        </div>
        <div className="form-row"><label className="form-label">Address</label><input type="text" className="form-input" id="asup-address" value={address ?? ''} onChange={(e) => setAddress(e.target.value)} /></div>
        <div className="form-row"><label className="form-label">Email</label><input type="email" className="form-input" id="asup-email" value={email ?? ''} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="form-row">
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" id="asup-pref" checked={preferred} onChange={(e) => setPreferred(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 14, height: 14 }} />
            Mark as Preferred Supplier
          </label>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="asup-save-btn" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Supplier'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
